import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bell, ChevronDown, User, LogOut, Settings, Building2, Menu, Sun, Moon } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useAppDispatch } from '@/state/hooks';
import { toggleMobileMenu } from '@/redux/slices/ui/uiSlice';
import { getInitials } from '@/utils/date/dateUtils';
import { getRoleName } from '@/constants';
import { organizationApi } from '@/api/organization/organization.api';
import { ORGANIZATIONS, ROLES } from '@/constants';
type OrganizationListItem = { id: string; name: string };

function findOrganization(
  list: OrganizationListItem[],
  organizationId?: string,
): OrganizationListItem | null {
  if (!organizationId) return null;
  return list.find((o) => o.id.toLowerCase() === organizationId.toLowerCase()) ?? null;
}

export function TopBar() {
  const { user, logout, setActiveOrganization } = useAuth();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setPreference } = useTheme();
  const toggleTheme = () => {
    setPreference(theme === 'light' ? 'dark' : 'light');
  };
  const [showNotif, setShowNotif] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [organizations, setOrganizations] = useState<OrganizationListItem[]>([...ORGANIZATIONS]);
  const [selectedOrg, setSelectedOrg] = useState<OrganizationListItem | null>(null);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [orgSaving, setOrgSaving] = useState(false);

  const profileRef = useRef<HTMLDivElement>(null);
  const orgRef = useRef<HTMLDivElement>(null);

  const fullName = user?.fullName || user?.firstName || 'User';
  const canSwitchOrganization = user?.role === ROLES.ADMIN;

  useEffect(() => {
    organizationApi
      .getAll()
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        const mapped: OrganizationListItem[] = list.map(
          (o: { id: string; name?: string; orgName?: string }) => ({
            id: o.id,
            name: o.name ?? o.orgName ?? '',
          }),
        );
        const items = mapped.length > 0 ? mapped : [...ORGANIZATIONS];
        setOrganizations(items);
        const activeOrg = findOrganization(items, user?.organizationId);
        if (activeOrg) setSelectedOrg(activeOrg);
      })
      .catch(() => {
        setOrganizations([...ORGANIZATIONS]);
        const activeOrg = findOrganization([...ORGANIZATIONS], user?.organizationId);
        if (activeOrg) setSelectedOrg(activeOrg);
      })
      .finally(() => setOrgsLoading(false));
  }, [user?.organizationId]);

  useEffect(() => {
    if (!selectedOrg && user?.organizationId) {
      const activeOrg = findOrganization(organizations, user.organizationId);
      if (activeOrg) setSelectedOrg(activeOrg);
    }
  }, [organizations, selectedOrg, user?.organizationId]);

  const handleOrganizationSelect = async (org: OrganizationListItem) => {
    setShowOrgDropdown(false);
    if (!canSwitchOrganization || org.id === selectedOrg?.id) return;

    setOrgSaving(true);
    try {
      await setActiveOrganization(org.id);
      setSelectedOrg(org);
      toast.success(`Switched to ${org.name}`);
    } catch {
      toast.error('Failed to switch organization');
    } finally {
      setOrgSaving(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setShowProfile(false);
      }
      if (orgRef.current && !orgRef.current.contains(e.target as Node)) {
        setShowOrgDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const organizationLabel = orgsLoading || orgSaving
    ? 'Loading...'
    : selectedOrg?.name ?? 'Organization';

  return (
    <header className="h-topbar bg-surface border-b border-border flex items-center gap-4 px-5 shrink-0 z-30">
      <button 
        className="md:hidden p-1.5 -ml-2 text-text-muted hover:text-brand-primary rounded-lg transition-colors"
        onClick={() => dispatch(toggleMobileMenu())}
      >
        <Menu size={20} />
      </button>

      <div className="flex items-center gap-2 ml-auto">

        {/* Organization */}
        {!['/config/org', '/config/organizations'].includes(location.pathname) && (
          <div className="relative hidden md:block" ref={orgRef}>
            {canSwitchOrganization ? (
              <>
                <button 
                  onClick={() => {
                    setShowOrgDropdown(!showOrgDropdown);
                    setShowProfile(false);
                    setShowNotif(false);
                  }}
                  disabled={orgsLoading || orgSaving}
                  className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-lg hover:bg-surface-muted text-sm text-text-primary disabled:opacity-60"
                >
                  <Building2 size={16} className="text-text-muted" />
                  <span className="truncate max-w-[140px]">{organizationLabel}</span>
                  <ChevronDown size={14} className="text-text-muted ml-1" />
                </button>
                
                {showOrgDropdown && (
                  <div className="absolute left-0 top-full mt-1.5 w-64 bg-surface border border-border rounded-xl shadow-lg z-50 py-1 overflow-hidden">
                    <div className="max-h-60 overflow-y-auto scrollbar-thin">
                      {organizations.map(org => (
                        <button
                          key={org.id}
                          onClick={() => handleOrganizationSelect(org)}
                          className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${selectedOrg?.id === org.id ? 'bg-brand-primary/10 text-brand-primary font-medium' : 'text-text-primary hover:bg-surface-muted'}`}
                        >
                          <div className="truncate">{org.name}</div>
                        </button>
                      ))}
                      {!orgsLoading && organizations.length === 0 && (
                        <div className="px-4 py-3 text-sm text-text-muted text-center">
                          No organizations found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-lg text-sm text-text-primary">
                <Building2 size={16} className="text-text-muted" />
                <span className="truncate max-w-[140px]">{organizationLabel}</span>
              </div>
            )}
          </div>
        )}

        {/* Notifications */}
        {/* <div className="relative">
          <button
            onClick={() => {
              setShowNotif(!showNotif);
              setShowProfile(false);
              setShowOrgDropdown(false);
            }}
            className="w-9 h-9 rounded-full flex items-center justify-center text-text-secondary hover:bg-surface-muted relative"
          >
            <Bell size={20} strokeWidth={1.5} />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-brand-red rounded-full border-2 border-white" />
          </button>
        </div> */}

        {/* Theme Toggle */}
        <button
          type="button"
          onClick={toggleTheme}
          className="w-9 h-9 rounded-full flex items-center justify-center text-text-secondary hover:bg-surface-muted transition-colors"
          title={`Switch to ${theme === 'light' ? 'Dark' : 'Light'} Mode`}
        >
          {theme === 'light' ? (
            <Moon size={20} strokeWidth={1.5} />
          ) : (
            <Sun size={20} strokeWidth={1.5} />
          )}
        </button>

        {/* Profile */}
        <div className="relative flex items-center gap-3 pl-3 border-l border-border" ref={profileRef}>
          <button
            type="button"
            onClick={() => {
              setShowProfile(!showProfile);
              setShowNotif(false);
              setShowOrgDropdown(false);
            }}
            className="flex items-center gap-3 rounded-lg hover:bg-surface-muted transition-colors pr-1"
            title="Profile menu"
          >
            <div className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center text-brand-primary text-sm font-bold">
              {getInitials(fullName)}
            </div>
            <div className="text-left hidden sm:flex flex-col justify-center">
              <div className="text-sm font-medium text-text-primary">{fullName}</div>
              <div className="inline-flex mt-0.5">
                <span className="px-2 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary text-[10px] font-semibold">
                  {getRoleName(user?.role)}
                </span>
              </div>
            </div>
            <ChevronDown size={14} className="text-text-muted hidden sm:block" />
          </button>
          <button
            type="button"
            onClick={() => logout()}
            className="p-1.5 ml-1 text-text-muted hover:text-brand-red rounded-lg transition-colors"
            title="Log Out"
          >
            <LogOut size={18} strokeWidth={1.5} />
          </button>

          {/* Profile dropdown */}
          {showProfile && (
            <div className="absolute right-0 top-full mt-1.5 w-56 bg-surface border border-border rounded-xl shadow-lg z-50 py-1 overflow-hidden">
              {/* User info header */}
              <div className="px-4 py-3 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-brand-primary flex items-center justify-center text-white text-sm font-bold shrink-0">
                    {getInitials(fullName)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-text-primary truncate">{fullName}</p>
                    <p className="text-2xs text-text-muted truncate">{user?.email}</p>
                  </div>
                </div>
              </div>

              {/* Menu items */}
              <div className="py-1">
                <button
                  onClick={() => { setShowProfile(false); navigate('/profile'); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-primary hover:bg-surface-muted transition-colors"
                >
                  <User size={15} className="text-text-muted" />
                  My Profile
                </button>
                {user?.role === ROLES.ADMIN && (
                  <button
                    onClick={() => { setShowProfile(false); navigate('/config/org'); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-primary hover:bg-surface-muted transition-colors"
                  >
                    <Settings size={15} className="text-text-muted" />
                    System Settings
                  </button>
                )}
              </div>

              {/* Sign out */}
              <div className="border-t border-border py-1">
                <button
                  onClick={() => { setShowProfile(false); logout(); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-brand-red hover:bg-red-50 transition-colors"
                >
                  <LogOut size={15} />
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
